/**
 * Interstitial policy, pure and free of Phaser, storage and the clock so the
 * simulation (scripts/ads-sim.ts) and the tests run the exact code the game runs.
 *
 * The rules are conservative on purpose. Sources and dates are in ADS-POLICY.md.
 */

export const AD_POLICY = {
  /** Endless levels 0..3 are always ad free: the next level index must be at least this. */
  minNextEndlessIndex: 4,
  /** At least this many finished endless levels since the last interstitial. */
  minLevelsBetween: 4,
  /** At least this long since the last interstitial. */
  minGapMs: 180_000,
  /** Never within this long of a failed level. */
  failQuietMs: 10_000,
} as const;

export interface AdState {
  levelsSinceAd: number;
  /** Epoch ms of the last interstitial, 0 = never. */
  lastInterstitialAt: number;
  /** Epoch ms of the last failed level, 0 = never. */
  lastFailAt: number;
}

export interface AdContext {
  now: number;
  /** Index of the endless level about to start. */
  nextEndlessIndex: number;
  /** True if the level about to start is a tutorial level. */
  nextIsTutorial: boolean;
  /** True if the transition replays or retries the level just played. */
  isReplay: boolean;
}

export type AdVerdict = { allow: true } | { allow: false; reason: string };

export function interstitialVerdict(s: AdState, c: AdContext): AdVerdict {
  if (c.nextIsTutorial) return { allow: false, reason: 'tutorial' };
  if (c.isReplay) return { allow: false, reason: 'replay' };
  if (c.nextEndlessIndex < AD_POLICY.minNextEndlessIndex) return { allow: false, reason: 'early-endless' };
  if (s.lastInterstitialAt === 0) return { allow: false, reason: 'seed-clock' };
  if (s.lastFailAt !== 0 && c.now - s.lastFailAt < AD_POLICY.failQuietMs) return { allow: false, reason: 'after-fail' };
  if (s.levelsSinceAd < AD_POLICY.minLevelsBetween) return { allow: false, reason: 'too-few-levels' };
  if (c.now - s.lastInterstitialAt < AD_POLICY.minGapMs) return { allow: false, reason: 'gap' };
  return { allow: true };
}
